// Copyright ©️ 2026 eVolve MEP, LLC

import React, { useCallback } from 'react';

import {
  isNotFound,
  type TableViewId,
  type TableViewType,
  type TableViewWithGridState,
  useEvolveGet,
} from '@evolvemep/api-ts';
import { Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import type { GridApi } from 'ag-grid-enterprise';

import { loadSavedGridState } from 'helpers/ag-grid/TableViewPanel/loadSavedGridState';
import { getTableSessionState, useTableSessionState } from 'helpers/ag-grid/TableViewPanel/useTableSessionState';
import { gridStatesEqual } from 'helpers/ag-grid/TableViewPanel/utils';
import { isNil } from 'helpers/isNotNil';

/**
 * Lets the user know when a table view they have unsaved changes on was changed or deleted by someone else.
 * The user's grid is never changed without them asking for it.
 */
export const useTableViewChangeCheck = (tableViewType: TableViewType) => {
  const { recordAppliedTableView, recordLatestSavedGridState, onTableViewDeleted } =
    useTableSessionState(tableViewType);
  const { apiCall: getTableViewApiCall } = useEvolveGet('admin/tableView/:tableViewId');

  const notifyIfTableViewUpdated = useCallback(
    (gridApi: GridApi, latestTableView: TableViewWithGridState) => {
      const { tableViewId, name, gridState: latestGridState } = latestTableView;
      const tableSessionState = getTableSessionState(tableViewType);
      if (
        tableSessionState?.selectedTableViewId !== tableViewId ||
        isNil(tableSessionState.savedGridState) ||
        gridStatesEqual(tableSessionState.savedGridState, latestGridState)
      ) {
        return;
      }

      recordLatestSavedGridState(tableViewId, latestGridState);
      const notificationId = `table-view-updated-${tableViewId}`;
      notifications.show({
        id: notificationId,
        title: 'Table view updated',
        message: (
          <>
            Table view {name} has been updated.{' '}
            <Text
              className="link"
              span
              onClick={() => {
                if (!gridApi.isDestroyed()) {
                  recordAppliedTableView(tableViewId, latestGridState);
                  loadSavedGridState(latestGridState, gridApi, true);
                }
                notifications.hide(notificationId);
              }}
            >
              Click here
            </Text>{' '}
            to load the latest version.
          </>
        ),
      });
    },
    [tableViewType, recordAppliedTableView, recordLatestSavedGridState],
  );

  const checkForTableViewChanges = useCallback(
    async (gridApi: GridApi, tableViewId: TableViewId) => {
      try {
        const latestTableView = await getTableViewApiCall({ url: `admin/tableView/${tableViewId}` });
        notifyIfTableViewUpdated(gridApi, latestTableView);
      } catch (error) {
        if (!isNotFound(error)) return;
        onTableViewDeleted(tableViewId);
        notifications.show({
          title: 'Table view deleted',
          message: 'Your selected table view no longer exists.',
        });
      }
    },
    [getTableViewApiCall, notifyIfTableViewUpdated, onTableViewDeleted],
  );

  return { checkForTableViewChanges, notifyIfTableViewUpdated };
};
